import { useLayoutEffect, useRef, useState } from "react";
import { useResizeObserver } from "./useResizeObserver";

const displaySizeVariants = [
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "4xl",
  "5xl",
  "6xl",
  "7xl",
] as const;

type DisplaySizeVariant = (typeof displaySizeVariants)[number];

const getDisplaySizeVariant = (
  width: number,
): DisplaySizeVariant | undefined => {
  switch (true) {
    case width >= 1280:
      return "7xl";
    case width >= 1152:
      return "6xl";
    case width >= 1024:
      return "5xl";
    case width >= 896:
      return "4xl";
    case width >= 768:
      return "3xl";
    case width >= 672:
      return "2xl";
    case width >= 576:
      return "xl";
    case width >= 512:
      return "lg";
    case width >= 448:
      return "md";
    case width >= 384:
      return "sm";
    case width >= 320:
      return "xs";
  }
};

export const useDisplaySizeVariant = () => {
  const target = useRef<HTMLDivElement>(null);
  const [displaySizeVariant, setDisplaySizeVariant] =
    useState<DisplaySizeVariant>();

  useLayoutEffect(() => {
    const element = target.current;
    if (!element) {
      return;
    }

    setDisplaySizeVariant(
      getDisplaySizeVariant(element.getBoundingClientRect().width),
    );
  }, []);

  useResizeObserver(target, (entry) => {
    setDisplaySizeVariant(getDisplaySizeVariant(entry.contentRect.width));
  });

  return {
    target,
    displaySizeVariant,
    isDisplaySizeSatisfied: (targetDisplaySizeVariant: DisplaySizeVariant) =>
      displaySizeVariants.indexOf(displaySizeVariant ?? "") >=
      displaySizeVariants.indexOf(targetDisplaySizeVariant),
  };
};
